import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  fruitSelectionOptions,
  getProduct,
  timeSlots,
  weekdays,
} from "../data";
import { getSchedule } from "../services/firestore";
import { isOrderModifiable } from "../utils/admin";
import { useApp } from "../store";
import {
  AddressCard,
  Brand,
  PageHeader,
  PrimaryButton,
} from "../components/UI";

import "../css/Schedule.css";
export function Schedule() {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setDraft, draft, addresses, user, deliveries } = useApp();
  const editScheduleId = new URLSearchParams(location.search).get(
    "editScheduleId",
  );
  const editOrderId = new URLSearchParams(location.search).get("editOrderId");
  const editMode = new URLSearchParams(location.search).get("editMode");
  const isOrderEditMode = editMode === "order" && Boolean(editOrderId);
  const isEditMode = Boolean(editScheduleId || isOrderEditMode);
  const product = getProduct(id);
  const fruit = type === "fruit_basket";
  const frequencies = fruit
    ? ["One Time", "Weekly", "2 Days a Week", "Monthly"]
    : ["One Time", "Daily", "3 Days a Week", "Weekly", "Monthly"];

  const savedDraft =
    draft?.productId === id && draft?.type === type ? draft : null;

  const [frequency, setFrequency] = useState(() =>
    savedDraft?.frequency && frequencies.includes(savedDraft.frequency)
      ? savedDraft.frequency
      : frequencies[0],
  );
  const [days, setDays] = useState(() => savedDraft?.days || []);
  const [slot, setSlot] = useState(
    () => savedDraft?.deliveryTimeSlot || timeSlots[2],
  );
  const [date, setDate] = useState(
    () =>
      savedDraft?.startDate ||
      new Date(Date.now() + 86400000).toISOString().slice(0, 10),
  );
  const [addressId, setAddressId] = useState(
    () => savedDraft?.addressId || undefined,
  );
  const [quantity, setQuantity] = useState(
    () => Number(savedDraft?.quantity) || 1,
  );
  const [selectedFoods, setSelectedFoods] = useState(() =>
    Array.isArray(savedDraft?.selectedFoods) && savedDraft.selectedFoods.length
      ? savedDraft.selectedFoods
      : fruit
        ? [product?.ingredients?.[0] || fruitSelectionOptions[0]]
        : [],
  );
  const [editLoading, setEditLoading] = useState(isEditMode);
  const [editError, setEditError] = useState("");
  // Edit mode loads the existing schedule instead of starting a new one.
  useEffect(() => {
    window.scrollTo(0, 0);
    let mounted = true;
    if (!editScheduleId || isOrderEditMode) {
      setEditLoading(false);
      return undefined;
    }

    setEditLoading(true);
    setEditError("");
    getSchedule(editScheduleId)
      .then((existing) => {
        if (!mounted) return;
        if (!existing) {
          setEditError(
            "This schedule could not be found. Please return to My deliveries and try again.",
          );
          return;
        }
        setFrequency(existing.frequency || frequencies[0]);
        setDays(Array.isArray(existing.days) ? existing.days : []);
        setSlot(existing.deliveryTimeSlot || timeSlots[2]);
        setDate(
          existing.startDate ||
            new Date(Date.now() + 86400000).toISOString().slice(0, 10),
        );
        setAddressId(existing.addressId);
        setQuantity(Number(existing.quantity) || 1);
        setSelectedFoods(
          Array.isArray(existing.selectedFoods) && existing.selectedFoods.length
            ? existing.selectedFoods
            : [product?.ingredients?.[0] || fruitSelectionOptions[0]],
        );
        setDraft({
          productId: existing.productId || id,
          type: existing.type || type,
          frequency: existing.frequency || frequencies[0],
          days: Array.isArray(existing.days) ? existing.days : [],
          startDate: existing.startDate,
          deliveryTimeSlot: existing.deliveryTimeSlot || timeSlots[2],
          addressId: existing.addressId,
          quantity: Number(existing.quantity) || 1,
          selectedFoods:
            Array.isArray(existing.selectedFoods) &&
            existing.selectedFoods.length
              ? existing.selectedFoods
              : [product?.ingredients?.[0] || fruitSelectionOptions[0]],
          editScheduleId,
          editOrderId,
        });
      })
      .catch((err) => {
        if (mounted)
          setEditError(err?.message || "Unable to load the existing schedule.");
      })
      .finally(() => {
        if (mounted) setEditLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [editScheduleId]);

  useEffect(() => {
    if (!isOrderEditMode || !editOrderId) return undefined;
    const existingOrder = deliveries.find((item) => item.id === editOrderId);
    if (!existingOrder) {
      setEditError(
        "This delivery could not be found. Please return to My deliveries and try again.",
      );
      setEditLoading(false);
      return undefined;
    }
    setFrequency(
      existingOrder.frequency ||
        (existingOrder.recurring ? "Weekly" : "One Time"),
    );
    setDays(Array.isArray(existingOrder.days) ? existingOrder.days : []);
    setSlot(existingOrder.timeSlot || timeSlots[2]);
    setDate(existingOrder.deliveryDate || date);
    setAddressId(existingOrder.addressId);
    setQuantity(Number(existingOrder.quantity) || 1);
    setDraft({
      productId: existingOrder.productId || id,
      type: existingOrder.type || type,
      frequency:
        existingOrder.frequency ||
        (existingOrder.recurring ? "Weekly" : "One Time"),
      days: Array.isArray(existingOrder.days) ? existingOrder.days : [],
      startDate: existingOrder.deliveryDate,
      deliveryTimeSlot: existingOrder.timeSlot || timeSlots[2],
      addressId: existingOrder.addressId,
      quantity: Number(existingOrder.quantity) || 1,
      selectedFoods:
        Array.isArray(existingOrder.selectedFoods) &&
        existingOrder.selectedFoods.length
          ? existingOrder.selectedFoods
          : [product?.ingredients?.[0] || fruitSelectionOptions[0]],
      editOrderId,
      editMode: "order",
    });
    setEditLoading(false);
    return undefined;
  }, [isOrderEditMode, editOrderId, deliveries]);

  // Keep the selected address stable while Firestore is loading.
  // The previous implementation cleared addressId when addresses was temporarily [].
  useEffect(() => {
    if (!addresses.length) return;

    if (!addressId) {
      setAddressId(addresses[0]?.id);
      return;
    }

    if (!addresses.some((address) => address.id === addressId)) {
      setAddressId(addresses[0]?.id);
    }
  }, [addresses, addressId]);

  // Persist the schedule draft continuously. This means navigating to
  // Addresses/Register and coming back does not reset the form.
  useEffect(() => {
    if (!product) return;

    setDraft({
      productId: id,
      type,
      frequency,
      days,
      startDate: date,
      deliveryTimeSlot: slot,
      addressId,
      quantity,
      ...(fruit ? { selectedFoods } : {}),
      ...(isEditMode
        ? {
            editScheduleId,
            editOrderId,
            ...(isOrderEditMode ? { editMode: "order" } : {}),
          }
        : {}),
    });
  }, [
    product,
    id,
    type,
    frequency,
    days,
    date,
    slot,
    addressId,
    quantity,
    selectedFoods,
    fruit,
    setDraft,
    isEditMode,
    editScheduleId,
    editOrderId,
    isOrderEditMode,
  ]);

  // When Addresses sends us back with a newly created address, select it.
  useEffect(() => {
    const selectedAddressId = new URLSearchParams(location.search).get(
      "addressId",
    );
    if (
      selectedAddressId &&
      addresses.some((address) => address.id === selectedAddressId)
    ) {
      setAddressId(selectedAddressId);
    }
  }, [location.search, addresses]);

  const toggleDay = (day) =>
    setDays((current) =>
      current.includes(day)
        ? current.filter((item) => item !== day)
        : [...current, day],
    );

  const invalidDays =
    (frequency === "2 Days a Week" && days.length !== 2) ||
    (frequency === "3 Days a Week" && days.length !== 3) ||
    (frequency === "Weekly" && days.length !== 1) ||
    (frequency === "Monthly" && days.length !== 1);

  const orderEditLocked =
    isOrderEditMode &&
    !isOrderModifiable({ deliveryDate: date, timeSlot: slot });
  const canReview = Boolean(
    !editLoading &&
    !editError &&
    !orderEditLocked &&
    addressId &&
    date &&
    slot &&
    quantity > 0 &&
    !invalidDays,
  );

  const addAddressReturnTo = `${location.pathname}${location.search}`;

  const proceed = () => {
    if (!addressId) {
      navigate(
        user?.uid
          ? `/addresses?returnTo=${encodeURIComponent(addAddressReturnTo)}`
          : `/register?redirect=${encodeURIComponent("/addresses?returnTo=" + addAddressReturnTo)}`,
      );
      return;
    }

    // setDraft is already kept up to date, but explicitly save once before navigation.
    setDraft({
      productId: id,
      type,
      frequency,
      days,
      startDate: date,
      deliveryTimeSlot: slot,
      addressId,
      quantity,
      ...(fruit ? { selectedFoods } : {}),
      ...(isEditMode
        ? {
            editScheduleId,
            editOrderId,
            ...(isOrderEditMode ? { editMode: "order" } : {}),
          }
        : {}),
    });

    const reviewPath = isEditMode
      ? `/order-review?editScheduleId=${encodeURIComponent(editScheduleId || "")}&editOrderId=${encodeURIComponent(editOrderId || "")}&editMode=${encodeURIComponent(isOrderEditMode ? "order" : "schedule")}`
      : "/order-review";

    navigate(
      user?.uid
        ? reviewPath
        : `/register?redirect=${encodeURIComponent(reviewPath)}`,
    );
  };

  return (
    <div className="schedule-page">
      <header className="flow-header">
        <Link
          to={fruit ? `/fruits/${id}` : `/protein-shakes/${id}`}
          className="icon-button"
        >
          <i className="fa-solid fa-arrow-left" />
        </Link>
        <Brand />
        <span className="step-label">Step 1 of 2</span>
      </header>

      <div className="container flow-container">
        <div className="flow-progress">
          <span className="active" />
          <span />
        </div>

        <PageHeader
          back={false}
          eyebrow={
            isEditMode
              ? isOrderEditMode
                ? "Update this delivery"
                : "Update your schedule"
              : "Set it once"
          }
          title={
            isEditMode
              ? isOrderEditMode
                ? "Change this delivery"
                : "Change your delivery schedule"
              : "When should we deliver?"
          }
          copy={
            isEditMode
              ? isOrderEditMode
                ? "Only this existing order will be updated. No new order will be created."
                : "Your existing recurring schedule will be updated — a new order will not be created."
              : "Choose a recurring routine that works for you. You can change or pause it later."
          }
        />

        {editError && <div className="alert alert-danger">{editError}</div>}
        {orderEditLocked && (
          <div className="alert alert-danger">
            This delivery is inside the 24-hour modification window. Its
            date/time cannot be changed.
          </div>
        )}
        {isEditMode && !editError && !orderEditLocked && (
          <div className="schedule-edit-notice">
            <i className="fa-solid fa-clock" />
            <div>
              <strong>24-hour modification policy</strong>
              <span>
                {isOrderEditMode
                  ? "This delivery can be changed only when it starts more than 24 hours from now. The recurring schedule and other deliveries are not changed."
                  : "Changes are allowed only more than 24 hours before a delivery. Locked deliveries remain unchanged. This schedule has one common delivery window, so changing the time changes the time for the schedule's future editable deliveries."}
              </span>
            </div>
          </div>
        )}

        <div className="schedule-layout">
          <div>
            <section className="form-section">
              <div className="form-section-title">
                <span>1</span>
                <div>
                  <h2>Frequency</h2>
                  <p>How often do you need this?</p>
                </div>
              </div>

              <div className="choice-grid">
                {frequencies.map((frequencyOption) => (
                  <button
                    type="button"
                    className={frequency === frequencyOption ? "selected" : ""}
                    key={frequencyOption}
                    onClick={() => {
                      setFrequency(frequencyOption);
                      if (
                        (frequencyOption === "Weekly" ||
                          frequencyOption === "Monthly") &&
                        days.length > 1
                      ) {
                        setDays(days.slice(0, 1));
                      }
                    }}
                  >
                    <i
                      className={`fa-regular ${
                        frequencyOption === "One Time"
                          ? "fa-calendar"
                          : "fa-calendar-check"
                      }`}
                    />
                    <span>{frequencyOption}</span>
                    {frequency === frequencyOption && (
                      <i className="fa-solid fa-circle-check check" />
                    )}
                  </button>
                ))}
              </div>
            </section>

            {frequency !== "One Time" && frequency !== "Daily" && (
              <section className="form-section">
                <div className="form-section-title scheduled-days-note">
                  <span>2</span>
                  <div>
                    <h2>Delivery days</h2>
                    <p>
                      {frequency === "2 Days a Week"
                        ? "Select exactly 2 days."
                        : frequency === "3 Days a Week"
                          ? "Select exactly three days."
                          : frequency === "Weekly"
                            ? "Select exactly 1 day."
                            : frequency === "Monthly"
                              ? "Select 1 weekday for the monthly delivery."
                              : "Pick the days that suit your routine."}
                    </p>
                  </div>
                </div>

                <div className="day-grid">
                  {weekdays.map((day) => (
                    <button
                      type="button"
                      className={days.includes(day) ? "selected" : ""}
                      key={day}
                      onClick={() => toggleDay(day)}
                    >
                      <span>{day.slice(0, 3)}</span>
                      <small>{day.slice(0, 1)}</small>
                    </button>
                  ))}
                </div>
              </section>
            )}

            <section className="form-section">
              <div className="form-section-title">
                <span>3</span>
                <div>
                  <h2>Start date</h2>
                  <p>When should this schedule begin?</p>
                </div>
              </div>

              <label className="form-label">Start from</label>
              <input
                type="date"
                className="form-control form-control-lg"
                min={new Date().toISOString().slice(0, 10)}
                value={date}
                onChange={(event) => setDate(event.target.value)}
              />
            </section>

            <section className="form-section">
              <div className="form-section-title">
                <span>4</span>
                <div>
                  <h2>Preferred delivery window</h2>
                  <p>
                    We’ll deliver within this window—not at an exact minute.
                  </p>
                </div>
              </div>

              <div className="slot-list">
                {timeSlots.map((timeSlot) => (
                  <button
                    type="button"
                    className={slot === timeSlot ? "selected" : ""}
                    key={timeSlot}
                    onClick={() => setSlot(timeSlot)}
                  >
                    <i className="fa-regular fa-clock" />
                    {timeSlot}
                    {slot === timeSlot && (
                      <i className="fa-solid fa-circle-check ms-auto" />
                    )}
                  </button>
                ))}
              </div>
            </section>

            <section className="form-section">
              <div className="form-section-title">
                <span>5</span>
                <div>
                  <h2>Delivery address</h2>
                  <p>Choose a serviceable Pune address.</p>
                </div>
              </div>

              <div className="d-grid gap-3">
                {addresses.map((address) => (
                  <AddressCard
                    key={address.id}
                    address={address}
                    selected={addressId === address.id}
                    onSelect={setAddressId}
                    actions={false}
                  />
                ))}
              </div>

              <Link
                to={`/addresses?returnTo=${encodeURIComponent(addAddressReturnTo)}`}
                className="btn btn-soft w-100 mt-3"
              >
                <i className="fa-solid fa-plus me-2" />
                Add another address
              </Link>
            </section>
          </div>

          <aside className="schedule-summary">
            <img src={product?.image} alt="" />

            <div>
              <span className="card-kicker">You’re scheduling</span>
              <h3>{product?.name}</h3>
              <p>₹{product?.price} per delivery</p>
            </div>

            <div className="quantity-control">
              <span>Quantity</span>
              <div>
                <button
                  type="button"
                  onClick={() =>
                    setQuantity((current) => Math.max(1, current - 1))
                  }
                >
                  −
                </button>
                <strong>{quantity}</strong>
                <button
                  type="button"
                  onClick={() => setQuantity((current) => current + 1)}
                >
                  +
                </button>
              </div>
            </div>

            <div className="service-note">
              <i className="fa-solid fa-leaf" />
              <span>
                {fruit
                  ? "Bought fresh for upcoming demand."
                  : "Prepared fresh for your delivery."}
              </span>
            </div>

            {invalidDays && (
              <p className="small text-danger">
                {frequency === "Weekly"
                  ? "Select exactly one delivery day for a weekly schedule."
                  : frequency === "2 Days a Week"
                    ? "Select exactly two delivery days."
                    : frequency === "3 Days a Week"
                      ? "Select exactly three delivery days."
                      : frequency === "Monthly"
                        ? "Select exactly one weekday for the monthly delivery."
                        : "Select the required delivery days to continue."}
              </p>
            )}

            {!addressId && (
              <p className="small text-danger">
                Please add or select a delivery address to continue.
              </p>
            )}

            <PrimaryButton
              className="w-100"
              disabled={!canReview}
              onClick={proceed}
              icon="fa-arrow-right"
            >
              Review schedule
            </PrimaryButton>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default Schedule;
